import { useMemo } from 'react';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { WaterfallSheetConfigProps } from './useWaterfallSheetConfig.props';
import { WaterfallSheetColumns, WaterfallSheetConfigParams } from './useWaterfallSheetConfig.types';
import { contentClass, headerClassRight, totalClass } from '../../constants';
import WaterfallParser from '../WaterfallParser/WaterfallParser';
import WaterfallRowConfig from '../WaterfallRowConfig/WaterfallRowConfig';

// A direct invalid URL has no previous scenario. Show its real security rows,
// but leave all calculated cells blank rather than presenting invented results.
export const createUnavailableWaterfallSheetConfig = ({
  validSecurities = [],
  companyName,
  relatedEntityId,
}: Pick<WaterfallSheetConfigProps, 'validSecurities' | 'companyName' | 'relatedEntityId'>) =>
  new SpreadsheetConfig<object, WaterfallSheetColumns, object>({
    rowConfig: [
      { readOnly: true, className: headerClassRight, largeHeader: true },
      ...validSecurities.map(security => ({
        securityName: security.name,
        readOnly: true,
        className: contentClass,
      })),
      { value: `${companyName} Totals`, readOnly: true, className: totalClass },
    ],
    columns: [
      { label: 'Security', keyName: 'securityName' },
      { label: 'Distribution', keyName: 'aggregateValue' },
      { label: 'Per Share', keyName: 'presentValue' },
    ],
    parser: params => {
      const cells = WaterfallParser(params);
      Object.entries(cells).forEach(([key, cell]) => {
        if (cell.key !== 'B1' && cell.key !== 'C1' && cell.columnLegend !== 'A') {
          cells[key] = { ...cell, expr: undefined, value: '', gridType: undefined, format: undefined };
        }
      });
      return cells;
    },
    isReadOnly: true,
    showTitlesColumn: false,
    name: `UnavailableWaterfallSheet.${relatedEntityId}`,
  } as WaterfallSheetConfigParams);

const useWaterfallSheetConfig = ({
  scenarioObject,
  validSecurities,
  companyName,
  relatedEntityId = null,
}: WaterfallSheetConfigProps) => {
  const sheetConfig = useMemo(() => {
    if (scenarioObject && validSecurities) {
      const nameSuffix = relatedEntityId ? `.${relatedEntityId}` : '';
      return new SpreadsheetConfig<object, WaterfallSheetColumns, object>({
        rowConfig: WaterfallRowConfig({ scenarioObject, validSecurities, companyName }),
        columns: [
          { label: 'Security', keyName: 'securityName' },
          { label: 'Distribution', keyName: 'aggregateValue' },
          { label: 'Per Share', keyName: 'presentValue' },
        ],
        parser: WaterfallParser,
        isReadOnly: true,
        showTitlesColumn: false,
        name: `WaterfallSheet${nameSuffix}`,
      } as WaterfallSheetConfigParams);
    }
    return undefined;
  }, [scenarioObject, validSecurities, companyName, relatedEntityId]);

  return { sheetConfig };
};

export default useWaterfallSheetConfig;
